<template>

  <b-container fluid>
    <!-- User Interface controls -->
    <b-modal ref="modal"
      :id="modalID"
      size="xl"
      no-close-on-esc
      no-close-on-backdrop
      hide-footer
      header-class="card-header-padding">
      <loading-spinner v-if="loading" />
      <b-card-group deck v-if="report_data && !loading && report_data.report_type==='summary'">
        <b-card class="text-center" header-tag="header" footer-tag="footer" style="width: 250px;">
          <template v-slot:header>
            <h6 class="mb-0" :id="report_data.id">{{ report_data.title }}
            </h6>
          </template>
          <template>
            <div v-if="typeof report_data.chart_data.data === 'number'" class="h4 m-0 p-0 text-center">
              <b-col style="font-size: 35px; color: #20A8D8">
                {{ abbreviateNum(report_data.chart_data.data, 1) }}
              </b-col>
            </div>
            <div v-else-if="Array.isArray(report_data.chart_data.data)" class="h4 m-0 p-0 text-center">
              <b-row style="font-size: 15px">
                <b-col v-for="d in report_data.chart_data.data" :key="d.data + d.unit" style="color: #B0B0B0">
                  {{ d.unit }}
                </b-col>
              </b-row>
              <b-row style="font-size: 20px">
                <b-col v-for="m in report_data.chart_data.data" :key="m.data + m.unit" style="color: #20A8D8">
                  {{ abbreviateNum(m.data, 1) }}
                </b-col>
              </b-row>
            </div>
            <div v-else class="h4 m-0 p-0 text-center">
              <b-col style="font-size: 35px">{{abbreviateNum(report_data.chart_data.data, 1) }}</b-col>
            </div>
          </template>
        </b-card>
      </b-card-group>
      <categorical-insight v-if="report_data && !loading && report_data.report_type==='chart'"
              :report-id="this.report_data.id"
              :dashboard-id="this.report_data.dashboard_id"
              :name="this.report_data.title"
              :description="this.report_data.description"
              :subtitle="this.report_data.chart_config.subtitle"
              :values="this.report_data.chart_data.data"
              :xlabel="this.report_data.chart_config.xlabel"
              :xlabeltype="this.report_data.chart_config.xlabeltype"
              :growthrate="this.report_data.chart_config.growthrate"
              :ylabel="this.report_data.chart_config.ylabel"
              :default-chart="this.report_data.chart_config.default_chart_type"
              :drilldown="this.report_data.chart_config.drill_down"
              :grouped="this.report_data.chart_config.grouped"
              :filter-details="this.report_data.chart_config.filter_details"
              :data-value-label="this.report_data.chart_config.data_value_label"
              :width="4"
              :isString=true>
      </categorical-insight>
      <map-chart v-if="report_data && !loading && report_data.report_type==='map'"
        primary
        :mapD="this.report_data.chart_data.data"
        :title="this.report_data.title"/>
      <growth-chart v-if="report_data && !loading && report_data.report_type==='growth_chart'"
                    primary
                    :data="this.report_data.chart_data.data"
                    :percentage="false"
                    :title="`${this.report_data.title}`"
                    :value-name="`New`"
                    :value-title="growthChartTitles.value"
                    :aggregation-name="`Cumulative`"
                    :aggregation-title="growthChartTitles.aggregation"/>
    </b-modal>
    <b-row>
      <b-col lg="6" class="my-1">
        <b-form-group
          label="Filter"
          label-for="filter-input"
          label-cols-sm="3"
          label-align-sm="right"
          label-size="sm"
          class="mb-0"
        >
          <b-input-group size="sm">
            <b-form-input
              id="filter-input"
              v-model="filter"
              type="search"
              placeholder="Type to Search"
            ></b-form-input>

            <b-input-group-append>
              <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Main table element -->
    <b-table
      :items="glossary_items"
      :fields="fields"
      :filter="filter"
      :filter-included-fields="filterOn"
      responsive="sm"
      @filtered="onFiltered"
    >
      <template #cell(show_report)="row">
        <b-button v-b-modal="modalID" @click="showReportSelected(row.item)" size="sm" variant="primary">
            <i class="fa fa-eye mr-1"></i>
            View
          </b-button>

      </template>

      <template #cell(index)="data">
        {{ data.index + 1 }}
      </template>
    </b-table>

  </b-container>
</template>

<script>
import axios from "axios";
import {formatMapData, fullURL, abbreviateNumber, formatDateText, sortByMonthYearDate, growthTitles} from "@/utils";
import store from "@/store";
import LoadingSpinner from "./LoadingSpinner";
import CategoricalModalInsight from './Insights/CategoricalModalInsight';
import CategoricalFilterForm from './Insights/CategoricalFilterForm';
import CategoricalInsight from "./Insights/CategoricalInsight";
import MapChart from './Insights/MapChart'
import GrowthChart from './Insights/GrowthChart'
import {round} from "lodash/math";



export default {
  components: {
      CategoricalFilterForm,
      CategoricalModalInsight,
      LoadingSpinner,
      CategoricalInsight,
      MapChart,
      GrowthChart
    },

  data() {
    return {
      glossary_data: null,
      glossary_items: [],
      selected_report: null,
      showModal: false,
      fields: [
        {key: 'index', label: "#"},
          {key: 'show_report', label: "View"},
          'title',
        {key: "report_type", label: "Type"},
          'description',
          {key: "dashboard", label: "Dashboards"}
        ],
      report_data: null,
      loading: true,

      totalRows: 1,
      filter: null,
      filterOn: []
    }
  },

  created() {
    this.glossary_data = store.state.general.glossary2
    if (this.glossary_data) {
      this.glossary_data.forEach(glossary => {
        this.glossary_items.push({
          title: glossary["report_title"],
          description: glossary["report_description"],
          report_type: glossary["report_type"],
          dashboard: glossary["dashboard_name"],
          report_id: glossary["report_id"],
          dashboard_id: glossary["dashboard_id"]
        })
      })
    } else {
      axios.get(
        fullURL(`/dashboards/glossary2/all`)
      ).then(({data}) => {
          this.glossary_data = data
          this.glossary_data.forEach(glossary => {
            this.glossary_items.push({
              title: glossary["report_title"],
              description: glossary["report_description"],
              report_type: glossary["report_type"],
              dashboard: glossary["dashboard_name"],
              report_id: glossary["report_id"],
              dashboard_id: glossary["dashboard_id"]
            })
          })
          store.state.general.glossary2 = data
        }
      )
    }
  },
  computed: {
    modalID() {
      return "modal-1";
    },
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.items.length
  },
  methods: {
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length
    },
    showReportSelected(item) {
      console.log("item", item)
      this.loading = true
      let request_data = "?dashboard_id="+item.dashboard_id
      axios.get(
        fullURL(`/dashboards/report/${item.report_id}` + request_data)
      ).then(({data}) => {
        this.report_data = data
        if (this.report_data.report_type === 'chart') {
          if (this.report_data.chart_config.grouped) {
          for (let d = 0; d < this.report_data.chart_data.data.categories.length; d++) {
            this.report_data.chart_data.data.categories[d] = formatDateText(this.report_data.chart_data.data.categories[d], this.report_data.chart_config.xlabeltype)
          }
        } else if(!(this.report_data.chart_config.default_chart_type === "stacked_column" || this.report_data.chart_config.default_chart_type === "heat_map"
            || this.report_data.chart_config.drill_down && this.report_data.chart_config.subtitle)) {
            if (this.report_data.chart_config.xlabeltype && this.report_data.chart_config.xlabeltype.toLowerCase() === "date") {
              this.report_data.chart_data.data.sort(sortByMonthYearDate)
            }
            this.report_data.chart_data.data = this.report_data.chart_data.data.map(e => ([formatDateText(e['_id'], this.report_data.chart_config.xlabeltype), round(e['count'])]))
          }
        } else if (this.report_data.report_type === 'map') {
          this.report_data.chart_data.data = formatMapData(this.report_data.chart_data.data)
        }


      }).catch(console.log)
        .finally(() => {
          this.loading = false;
        })
    },

    abbreviateNum(number, precision) {
      return abbreviateNumber(number, precision)
    },

    growthChartTitles() {
      return growthTitles()
    }
  }
}
</script>
